import { useEffect, useState, useRef } from 'react';
import { Spin, Popover, Button, Drawer, Tooltip, Form, Input, message, Popconfirm } from 'antd';
import { useNavigate } from 'react-router-dom';
import styles from './index.module.scss'
import { getVideoList } from '../../api/video';
import { AxiosError, AxiosResponse } from 'axios';
import type { VideoItem } from '../../types/video';
import VideoPlayer from '../components/VideoPlayer';
import {
	DownloadOutlined,
	InfoCircleOutlined,
	CheckOutlined,
	CloseOutlined,
	EditOutlined,
	DeleteOutlined,
	ExclamationCircleFilled
} from '@ant-design/icons';
import { handleUrlDownload } from '../../utils';
import { sqlInjectionPattern } from '../../utils';
import { updateVideoName } from '../../api/video';
import { getVoiceProcessing } from '../../api/voice';
import { deleteVideo } from '../../api/video';
import { useTranslation } from 'react-i18next';


const Videos = () => {
	const navigate = useNavigate();
	const pageSize: number = 10

	const [videoHasMore, setVideoHasMore] = useState<boolean>(true)
	const [lastVideoId, setLastVideoId] = useState<number>(0)
	const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
	const [currentVideoList, setCurrentVideoList] = useState<VideoItem[]>([])
	const [selectedVideo, setSelectedVideo] = useState<VideoItem | null>(null)
	const [isNameEditing, setIsNameEditing] = useState<boolean>(false);
	const [editVideoName, setEditVideoName] = useState<string>('');
	const [isNameEditSubmitting, setIsNameEditSubmitting] = useState<boolean>(false)
	// 1. 下载视频 2. 下载音频 3. 删除视频
	const [loadings, setLoadings] = useState<boolean[]>([false, false, false, false])
	const [videoCountdown, setVideoCountdown] = useState<number>(0)
	const [voiceCountdown, setVoiceCountdown] = useState<number>(0)
	const videoTimerRef = useRef<number|null>(null);
	const voiceTimerRef = useRef<number|null>(null);
	const { t } = useTranslation()


	useEffect(() => {
		fetchVideoList();
	}, [])

	useEffect(() => {
		return () => {
			if (videoTimerRef.current) {
				window.clearInterval(videoTimerRef.current);
				videoTimerRef.current = null
			}
			if (voiceTimerRef.current) {
				window.clearInterval(voiceTimerRef.current);
				voiceTimerRef.current = null
			}
		};
	}, []);

	const fetchVideoList = () => {
		if (loadings[1] || !videoHasMore) return
		enterLoading(1)

		const lastId = lastVideoId
		const params = {
			pageSize,
			...(lastId ? { lastId } : {})
		};
		try {
			getVideoList(params).then((res: AxiosResponse) => {
				if (res.data.status === 200) {
					const newVideos: VideoItem[] = res.data.data.items

					setCurrentVideoList(prev => [...prev, ...newVideos]);
					setVideoHasMore(res.data.data.hasMore);
					if (newVideos.length > 0) {
						setLastVideoId(newVideos[newVideos.length - 1].id)
					}
					enterLoading(1, false)
				} else {
					enterLoading(1, false)
				}
			}).catch((err: AxiosError) => {
				enterLoading(1, false)
			})
		} finally {
			// enterLoading(1, false)
		}
	}

	const enterLoading = (index: number, status: boolean = true) => {

		setLoadings((prevLoadings) => {
			const newLoadings = [...prevLoadings];
			newLoadings[index] = status;
			return newLoadings;
		});
	}

	const handleLoadVideo = () => {
		fetchVideoList()
	}

	const handleShowVideoDetails = (video: VideoItem) => {
		setSelectedVideo(video);
		setDrawerOpen(true);
	}
	
	const startCountdown = (type: string) => {
		const countdown = 20;
		const setCountdown = type === 'video' ? setVideoCountdown : setVoiceCountdown;
		const timerRef = type === 'video' ? videoTimerRef : voiceTimerRef;
		
		setCountdown(countdown);

		if (timerRef.current) {
			window.clearInterval(timerRef.current)
		}
		
		timerRef.current = window.setInterval(() => {
			setCountdown(prev => {
			if (prev <= 1) {
				if (timerRef.current) window.clearInterval(timerRef.current);
				timerRef.current = null;
				return 0;
			}
			return prev - 1;
			});
		}, 1000);
	};

	const handleVideoDownload = () => {
		try {
			if (selectedVideo) {
				enterLoading(1)
				message.success("已开始下载")
				handleUrlDownload(selectedVideo.videoUrl)
				enterLoading(1, false)
				startCountdown('video');
			}
		} finally {
			
		}


	}
	const handleVoiceDownload = () => {
		// if (selectedVideo) 
		// console.log(selectedVideo)
		if (selectedVideo !== null) {
			const vid = selectedVideo.voiceId
			if (vid) {
				enterLoading(2)
				getVoiceProcessing({ 'id': vid }).then((res: AxiosResponse) => {
					if (res.data.status === 200) {
						// console.log(res.data.statusId)
						if (res.data.data.statusId === 2) {
							message.success("已开始下载")
							console.log('download')
							handleUrlDownload(res.data.data.voiceUrl)
							startCountdown('voice');
						} else {
							message.info("还未完成处理")
						}

					}
					enterLoading(2, false)
				}).catch((error) => {
					enterLoading(2, false)
				})
			}
		}
	}

	const handleVideoNameCancel = () => {
		setIsNameEditing(false);
	};

	const validateInputSafety = (input: string): boolean => {
		if (sqlInjectionPattern.test(input)) {
			message.error('输入内容不能包含特殊字符');
			return false;
		}

		// Additional validation rules
		if (input.length > 100) {
			message.error('名称长度不能超过100个字符');
			return false;
		}

		return true;
	};

	const handleVideoNameSave = async () => {
		if (!selectedVideo) return;

		const trimmedName = editVideoName.trim();

		if (!trimmedName) {
			message.error('视频名称不能为空');
			return;
		}

		if (!validateInputSafety(trimmedName)) {
			return;
		}

		try {
			setIsNameEditSubmitting(true);
			const videoId = selectedVideo.id
			const res = await updateVideoName({ 'id': videoId, 'name': trimmedName });

			if (res.data.status === 200) {
				message.success('视频名称更新成功');
				setIsNameEditing(false);
				const objIndex = currentVideoList.findIndex(obj => obj.id === videoId)
				var tmp = [...currentVideoList]
				tmp[objIndex].name = trimmedName
				setCurrentVideoList(tmp)

			} else {
				message.error('视频名称更新失败');
			}
		} catch (error) {
			// console.error('更新视频名称出错:', error);
			message.error('更新视频名称时发生错误');
		} finally {
			setIsNameEditSubmitting(false);
		}
	};

	const handleVideoNameEdit = () => {
		setEditVideoName(selectedVideo?.name || '');
		setIsNameEditing(true);
	};

	const handleDeleteVideo = () => {
		// console.log("selectedVideo: ", selectedVideo)
		if (selectedVideo === null) {
			message.error("视频信息错误")
			return
		}
		enterLoading(3)

		deleteVideo({ 'id': selectedVideo.id }).then((res: AxiosResponse) => {
			if (res.data.status === 200) {

				setCurrentVideoList(prevList =>
					prevList.filter(video => video.id !== selectedVideo.id)
				)
				message.success("删除成功")
			} else {
				message.error("删除失败")
			}
			enterLoading(3, false)
		}).catch(() => {
			enterLoading(3, false)
		})

	}

	return (
		<>

			<div className={styles.section}>
				<div className={styles.sectionHeader}>
					<div className={styles.title}>{t('video.title1')}</div>

				</div>
				<Spin spinning={loadings[1]}>
					<div className={styles.videoGrid}>
						{currentVideoList.map((video: VideoItem) => (

							<div key={video.id} >
								{
									video.processStatus === 2 ?
										(<div className={styles.videoCard}>
											<VideoPlayer src={video.videoUrl} width={200} maxHeight={200} />
											<div className={styles.videoCardInfo} onClick={() => handleShowVideoDetails(video)}>
												<div className={styles.videoName} >
													{video.name || t('untitled')}
												</div>
												<div className={styles.videoMeta}>
													<span>{video.createTime && video.createTime.slice(5, 10)}</span>
													<Tooltip title={t('view_details')}>
														<InfoCircleOutlined className={styles.infoIcon} />
													</Tooltip>
												</div>
											</div>
										</div>
										) :
										video.processStatus === 1 ?
											<div className={styles.loadCard} >{t('video.processing')}</div> :
											<div className={styles.loadCard} onClick={() => handleShowVideoDetails(video)}>{t('video.waiting_in_line')}</div>
								}
							</div>

						))}
						{
							videoHasMore && currentVideoList.length > 0 &&
							<div className={styles.loadCard} onClick={handleLoadVideo}>
								{t('load_more')}
							</div>
						}
						{currentVideoList.length === 0 && <div className={styles.loadCard}>{t('video.no_videos_yet')}</div>}
					</div>
				</Spin>
			</div>

			<Drawer
				title={t('view_details')}
				placement="right"
				size="default"
				onClose={() => setDrawerOpen(false)}
				open={drawerOpen}
			>
				{selectedVideo && (
					<div className={styles.videoDetails}>

						<div className={styles.videoInfo}>
							<div className={styles.titleRow}>
								{isNameEditing ? (
									<div className={styles.editNameContainer}>
										<Form.Item
											validateStatus={
												sqlInjectionPattern.test(editVideoName) ? 'error' : ''
											}
											help={
												sqlInjectionPattern.test(editVideoName)
													? t('form.help.sqlinject.tip')
													: ''
											}
											className={styles.editFormItem}
										>
											<Input
												value={editVideoName}
												onChange={(e) => setEditVideoName(e.target.value)}
												placeholder={t('form.edit.input_placehoder')}
												maxLength={30}
												className={styles.nameInput}
												autoFocus
											/>
										</Form.Item>
										<div className={styles.editActions}>
											<Button
												type="primary"
												icon={<CheckOutlined />}
												size="small"
												onClick={handleVideoNameSave}
												loading={isNameEditSubmitting}
												className={styles.editButton}
											/>
											<Button
												icon={<CloseOutlined />}
												size="small"
												onClick={handleVideoNameCancel}
												className={styles.editButton}
											/>
										</div>
									</div>
								) : (
									<div className={styles.titleWithEdit}>
										<div className={styles.name}>{selectedVideo.name || t('untitled')}</div>
										<Tooltip title={t('video.edit.title')}>
											<Button
												type="text"
												icon={<EditOutlined />}
												onClick={handleVideoNameEdit}
												className={styles.editNameButton}
											/>
										</Tooltip>
										{selectedVideo.processStatus === 2 &&
											<Popconfirm
												title={t('popconfirm.title')}
												placement="left"
												description={t('popconfirm.description')}
												onConfirm={handleDeleteVideo}
												
											>
												<Tooltip title={t('popconfirm.video.del_tip')}>

													<Button variant="text" color="danger" icon={<DeleteOutlined />} className={styles.deleteButton} loading={loadings[3]} />
												</Tooltip>
											</Popconfirm>
										}
									</div>
								)}


							</div>

							<div className={styles.infoRow}>
								<span className={styles.infoLabel}>{t('video.drawer.creation_time')}:</span>
								<span>{selectedVideo.createTime}</span>
							</div>

							<div className={styles.infoRow}>
								<span className={styles.infoLabel}>{t('video.drawer.process_status')}:</span>
								<span>
									{selectedVideo.processStatus === 2 ? t('video.finished') :
										selectedVideo.processStatus === 1 ? t('video.processing') : t('video.waiting')}
								</span>
							</div>

							<div className={styles.downloadActions}>
								<Button
									type="primary"
									icon={<DownloadOutlined />}
									onClick={handleVideoDownload}
									disabled={selectedVideo.processStatus !== 2 || videoCountdown > 0}
									loading={loadings[1]}
								>
									{videoCountdown > 0 ? `${t('video.download')} (${videoCountdown})` : t('video.download')}
								</Button>

								<Button
									icon={<DownloadOutlined />}
									onClick={handleVoiceDownload}
									disabled={selectedVideo.processStatus !== 2 || voiceCountdown > 0}
									loading={loadings[2]}
								>
									{voiceCountdown > 0 ? `${t('voice.download')} (${voiceCountdown})` : t('voice.download')}
								</Button>
							</div>
						</div>
					</div>
				)}
			</Drawer>
		</>

	)

}

export default Videos